import type { LoopDef, AmbientId, MusicId, OscNote } from './types';

const N = {
  C3: 130.81, E3: 164.81, G3: 196.0, A3: 220.0, B3: 246.94,
  C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.0, A4: 440.0, B4: 493.88,
  C5: 523.25, D5: 587.33, E5: 659.25, G5: 783.99,
};

function pad(freq: number, startMs: number, durationMs: number, gain = 0.15): OscNote {
  return {
    freq,
    durationMs,
    type: 'sine',
    startOffsetMs: startMs,
    attackMs: 200,
    releaseMs: 600,
    filterFreq: 1200,
    gain,
  };
}

function tone(freq: number, startMs: number, durationMs: number, gain = 0.12): OscNote {
  return {
    freq,
    durationMs,
    type: 'triangle',
    startOffsetMs: startMs,
    attackMs: 50,
    releaseMs: 200,
    filterFreq: 2000,
    gain,
  };
}

const LOOPS: Record<AmbientId | MusicId, LoopDef> = {
  // ===== AMBIENT por bioma =====
  meadow: {
    id: 'meadow',
    category: 'ambient',
    loopDurationMs: 12000,
    gain: 0.5,
    pattern: [
      pad(N.C3, 0, 6000),
      pad(N.G3, 0, 6000),
      tone(N.E5, 1000, 600),
      tone(N.G5, 2200, 600),
      tone(N.C5, 3400, 800),
      pad(N.A3, 6000, 6000),
      pad(N.E3, 6000, 6000),
      tone(N.D5, 7000, 600),
      tone(N.G5, 8200, 600),
      tone(N.E5, 9400, 800),
    ],
  },
  forest: {
    id: 'forest',
    category: 'ambient',
    loopDurationMs: 14000,
    gain: 0.45,
    pattern: [
      pad(N.D4, 0, 7000, 0.15),
      pad(N.A3, 0, 7000, 0.13),
      tone(N.D5, 1500, 800, 0.1),
      tone(N.F4, 3500, 800, 0.1),
      pad(N.C4, 7000, 7000, 0.15),
      pad(N.G3, 7000, 7000, 0.13),
      tone(N.A4, 8500, 800, 0.1),
      tone(N.D5, 10500, 800, 0.1),
    ],
  },
  cave: {
    id: 'cave',
    category: 'ambient',
    loopDurationMs: 16000,
    gain: 0.4,
    pattern: [
      pad(N.A3, 0, 8000, 0.18),
      pad(N.C4, 0, 8000, 0.14),
      tone(N.E4, 4000, 1000, 0.08),
      pad(N.E3, 8000, 8000, 0.18),
      pad(N.A3, 8000, 8000, 0.14),
      tone(N.A4, 12000, 1000, 0.08),
    ],
  },
  coast: {
    id: 'coast',
    category: 'ambient',
    loopDurationMs: 12000,
    gain: 0.5,
    pattern: [
      pad(N.F4, 0, 6000, 0.16),
      pad(N.C4, 0, 6000, 0.14),
      tone(N.A4, 2000, 800, 0.1),
      tone(N.C5, 3500, 800, 0.1),
      pad(N.G3, 6000, 6000, 0.16),
      pad(N.D4, 6000, 6000, 0.14),
      tone(N.B4, 8000, 800, 0.1),
      tone(N.D5, 9500, 800, 0.1),
    ],
  },
  'house-day': {
    id: 'house-day',
    category: 'ambient',
    loopDurationMs: 10000,
    gain: 0.45,
    pattern: [
      pad(N.C4, 0, 5000, 0.15),
      pad(N.G3, 0, 5000, 0.13),
      tone(N.E5, 1000, 500, 0.12),
      tone(N.G5, 2000, 500, 0.12),
      tone(N.C5, 3000, 700, 0.12),
      pad(N.F4, 5000, 5000, 0.15),
      pad(N.A3, 5000, 5000, 0.13),
      tone(N.A4, 6000, 500, 0.12),
      tone(N.C5, 7000, 500, 0.12),
      tone(N.E5, 8000, 700, 0.12),
    ],
  },
  'house-night': {
    id: 'house-night',
    category: 'ambient',
    loopDurationMs: 14000,
    gain: 0.35,
    pattern: [
      pad(N.A3, 0, 7000, 0.18),
      pad(N.E3, 0, 7000, 0.15),
      tone(N.E5, 3000, 1500, 0.08),
      pad(N.D4, 7000, 7000, 0.18),
      pad(N.G3, 7000, 7000, 0.15),
      tone(N.A4, 10000, 1500, 0.08),
    ],
  },
  // ===== MUSIC =====
  combat: {
    id: 'combat',
    category: 'music',
    loopDurationMs: 8000,
    gain: 0.5,
    pattern: [
      // bass square
      { freq: N.E3, durationMs: 200, type: 'square', startOffsetMs: 0, attackMs: 5, releaseMs: 50, gain: 0.18 },
      { freq: N.E3, durationMs: 200, type: 'square', startOffsetMs: 500, attackMs: 5, releaseMs: 50, gain: 0.18 },
      { freq: N.G3, durationMs: 200, type: 'square', startOffsetMs: 1000, attackMs: 5, releaseMs: 50, gain: 0.18 },
      { freq: N.E3, durationMs: 200, type: 'square', startOffsetMs: 1500, attackMs: 5, releaseMs: 50, gain: 0.18 },
      { freq: N.A3, durationMs: 200, type: 'square', startOffsetMs: 2000, attackMs: 5, releaseMs: 50, gain: 0.18 },
      { freq: N.E3, durationMs: 200, type: 'square', startOffsetMs: 2500, attackMs: 5, releaseMs: 50, gain: 0.18 },
      { freq: N.G3, durationMs: 200, type: 'square', startOffsetMs: 3000, attackMs: 5, releaseMs: 50, gain: 0.18 },
      { freq: N.B3, durationMs: 200, type: 'square', startOffsetMs: 3500, attackMs: 5, releaseMs: 50, gain: 0.18 },
      // lead arpeggio
      { freq: N.E5, durationMs: 100, type: 'triangle', startOffsetMs: 250, attackMs: 3, releaseMs: 30, gain: 0.13 },
      { freq: N.G5, durationMs: 100, type: 'triangle', startOffsetMs: 750, attackMs: 3, releaseMs: 30, gain: 0.13 },
      { freq: N.B4, durationMs: 100, type: 'triangle', startOffsetMs: 1250, attackMs: 3, releaseMs: 30, gain: 0.13 },
      { freq: N.E5, durationMs: 100, type: 'triangle', startOffsetMs: 1750, attackMs: 3, releaseMs: 30, gain: 0.13 },
      { freq: N.A4, durationMs: 100, type: 'triangle', startOffsetMs: 2250, attackMs: 3, releaseMs: 30, gain: 0.13 },
      { freq: N.E5, durationMs: 100, type: 'triangle', startOffsetMs: 2750, attackMs: 3, releaseMs: 30, gain: 0.13 },
      { freq: N.G5, durationMs: 100, type: 'triangle', startOffsetMs: 3250, attackMs: 3, releaseMs: 30, gain: 0.13 },
      { freq: N.B4, durationMs: 200, type: 'triangle', startOffsetMs: 3750, attackMs: 3, releaseMs: 60, gain: 0.13 },
      // segunda metade idêntica (loop)
      { freq: N.E3, durationMs: 200, type: 'square', startOffsetMs: 4000, attackMs: 5, releaseMs: 50, gain: 0.18 },
      { freq: N.E3, durationMs: 200, type: 'square', startOffsetMs: 4500, attackMs: 5, releaseMs: 50, gain: 0.18 },
      { freq: N.G3, durationMs: 200, type: 'square', startOffsetMs: 5000, attackMs: 5, releaseMs: 50, gain: 0.18 },
      { freq: N.E3, durationMs: 200, type: 'square', startOffsetMs: 5500, attackMs: 5, releaseMs: 50, gain: 0.18 },
      { freq: N.A3, durationMs: 200, type: 'square', startOffsetMs: 6000, attackMs: 5, releaseMs: 50, gain: 0.18 },
      { freq: N.E3, durationMs: 200, type: 'square', startOffsetMs: 6500, attackMs: 5, releaseMs: 50, gain: 0.18 },
      { freq: N.G3, durationMs: 200, type: 'square', startOffsetMs: 7000, attackMs: 5, releaseMs: 50, gain: 0.18 },
      { freq: N.B3, durationMs: 200, type: 'square', startOffsetMs: 7500, attackMs: 5, releaseMs: 50, gain: 0.18 },
    ],
  },
};

export function getLoopDef(id: AmbientId | MusicId): LoopDef {
  return LOOPS[id];
}
